import { apiFetch, ApiError } from './client'
import type {
  AdminAccount,
  AdminAccountDetail,
  AdminApplication,
  AdminApplicationDetail,
  AdminNotificationSettings,
  AdminPagination,
} from './adminTypes'

export interface AdminMe {
  id: string
  email: string
  role: 'admin' | 'viewer'
}

// Wraps apiFetch for every admin-data call (not auth() itself, not the
// applicant API's own apiFetch usages): a 401 here means the session died
// mid-use — expired, revoked, or logged out in another tab — and there is no
// page-level recovery from that except going back to /login. Doing this once
// here means an individual page's error state only has to handle genuine
// failures (network error, 500), not also re-derive "was this actually an
// auth problem" from the errors admin.ts sends called.
async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, init)
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      window.location.assign('/admin/login')
      // Never resolves: the page is navigating away, so no caller's .then()
      // should run and render with no data.
      return new Promise<T>(() => {})
    }
    throw err
  }
}

export function adminMe(): Promise<AdminMe> {
  return apiFetch('/api/v1/admin/auth/me')
}

export function adminLogout(): Promise<{ ok: boolean }> {
  return apiFetch('/api/v1/admin/auth/logout', { method: 'POST' })
}

export function listAccounts(offset = 0, limit = 0): Promise<{ accounts: AdminAccount[]; pagination: AdminPagination }> {
  const params = new URLSearchParams({ offset: String(offset) })
  if (limit) params.set('limit', String(limit))
  return adminFetch(`/api/v1/admin/accounts?${params}`)
}

export function getAccount(id: string): Promise<AdminAccountDetail> {
  return adminFetch(`/api/v1/admin/accounts/${id}`)
}

export function listApplications(
  status?: 'in_progress' | 'submitted',
  offset = 0,
  limit = 0,
): Promise<{ applications: AdminApplication[]; pagination: AdminPagination }> {
  const params = new URLSearchParams({ offset: String(offset) })
  if (status) params.set('status', status)
  if (limit) params.set('limit', String(limit))
  return adminFetch(`/api/v1/admin/applications?${params}`)
}

export function getApplication(id: string): Promise<AdminApplicationDetail> {
  return adminFetch(`/api/v1/admin/applications/${id}`)
}

// updateSnapshot sends the complete replacement form_snapshot (whole
// document, not a partial patch — see UpdateApplicationSnapshotForAdmin's
// doc comment in the Go query). role: admin only; a viewer's request 403s.
export function updateSnapshot(id: string, snapshot: Record<string, unknown>): Promise<AdminApplicationDetail> {
  return adminFetch(`/api/v1/admin/applications/${id}/snapshot`, {
    method: 'PATCH',
    body: JSON.stringify(snapshot),
  })
}

// resend queues a new Salesforce push against the application's *current*
// form_snapshot (call updateSnapshot first if the edit is meant to apply).
// Fire-and-forget server-side: the response confirms the push was queued,
// not that it succeeded — reload getApplication a moment later to see the
// new attempt.
export function resend(id: string): Promise<{ queued: boolean }> {
  return adminFetch(`/api/v1/admin/applications/${id}/resend`, { method: 'POST' })
}

export function getNotificationSettings(): Promise<AdminNotificationSettings> {
  return adminFetch('/api/v1/admin/settings/notifications')
}

// The list-specific endpoints (rather than one generic addRecipient(list,
// email)) mirror the backend's own separate routes/role gate per list —
// keeps a caller from being able to pass an arbitrary list_name string.
export function addSubmissionRecipient(email: string): Promise<AdminNotificationSettings> {
  return adminFetch('/api/v1/admin/settings/notifications/submission', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export function removeSubmissionRecipient(email: string): Promise<AdminNotificationSettings> {
  return adminFetch(`/api/v1/admin/settings/notifications/submission/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  })
}

export function addFailureRecipient(email: string): Promise<AdminNotificationSettings> {
  return adminFetch('/api/v1/admin/settings/notifications/failure', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

// The failure list must never end up empty (issue #78 decision #5) — the
// backend 409s ("list_empty") when this would remove its last recipient.
export function removeFailureRecipient(email: string): Promise<AdminNotificationSettings> {
  return adminFetch(`/api/v1/admin/settings/notifications/failure/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  })
}
